import { AuthError, type AuthResponse } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { isMissingRpc, removePrivateAudio } from './privateAudio';
import { meetsPasswordPolicy } from '@/screens/auth/validation';
import type { TFunction } from 'i18next';
import { currentUiLocale } from '@/lib/authLocale';
import { mfaErrorAction, parseMfaStatus, type MfaErrorAction, type MfaStatus } from '@/lib/mfaGate';

// Port of lib/repositories/auth_repository.dart.
//
// Translates Supabase auth/postgrest errors into an AuthFailureKind code;
// the UI calls `localizeAuthFailure` with the active i18next `t` to render
// it in the current language (the Dart `AuthFailure.localized(l)` analog).

export type AuthFailureKind =
  | 'invalidLogin'
  | 'emailTaken'
  | 'passwordTooShort'
  | 'notSignedIn'
  /** Credentials match but the email link hasn't been clicked yet. */
  | 'emailNotConfirmed'
  /** GoTrue needs the two-factor code before this change (insufficient_aal). */
  | 'mfaRequired'
  | 'unknown';

export class AuthFailure extends Error {
  readonly kind: AuthFailureKind;
  /** Raw provider message, only used for `unknown`. */
  readonly raw?: string;

  constructor(kind: AuthFailureKind, raw?: string) {
    super(raw ?? kind);
    this.name = 'AuthFailure';
    this.kind = kind;
    this.raw = raw;
  }
}

/** Resolve the user-facing message for an AuthFailure via i18next. */
export function localizeAuthFailure(f: AuthFailure, t: TFunction): string {
  switch (f.kind) {
    case 'invalidLogin':
      return t('exceptionInvalidLogin');
    case 'emailTaken':
      return t('exceptionEmailTaken');
    case 'passwordTooShort':
      return t('exceptionPasswordTooShort');
    case 'notSignedIn':
      return t('errorNotAuthenticated');
    case 'emailNotConfirmed':
      return t('exceptionEmailNotConfirmed');
    case 'mfaRequired':
      return t('mfaChallengeBodyReset');
    case 'unknown':
      return f.raw ?? t('unexpectedError', { error: '' });
  }
}

function toFailure(e: AuthError): AuthFailure {
  const lower = e.message.toLowerCase();
  if (lower.includes('invalid login')) return new AuthFailure('invalidLogin');
  if (lower.includes('already registered')) return new AuthFailure('emailTaken');
  // GoTrue's weak_password rejection (min length and/or required character
  // classes). The error_code is the reliable signal; the message wording
  // ("Password should be at least 8 characters…") is a fallback.
  if (
    (e as { code?: string }).code === 'weak_password' ||
    (lower.includes('password') &&
      (lower.includes('should') ||
        lower.includes('at least') ||
        lower.includes('weak') ||
        lower.includes('character')))
  ) {
    return new AuthFailure('passwordTooShort');
  }
  return new AuthFailure('unknown', e.message);
}

/** Maps SQLSTATEs from change_my_password / delete_my_account RPCs. */
function toFailureFromPostgrest(code: string | undefined, message: string): AuthFailure {
  switch (code) {
    case '28P01':
      return new AuthFailure('invalidLogin');
    case '22023':
      return new AuthFailure('passwordTooShort');
    case 'P0001':
      return new AuthFailure('notSignedIn');
    default:
      return new AuthFailure('unknown', message);
  }
}

export const authRepository = {
  async signIn(email: string, password: string): Promise<AuthResponse['data']> {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw toFailure(error);
    // Belt-and-suspenders: refuse a session for an unconfirmed user and drop
    // it client-side, so the auth listener never sees a half-trusted state.
    const user = data.user;
    if (user && !user.email_confirmed_at) {
      await supabase.auth.signOut();
      throw new AuthFailure('emailNotConfirmed');
    }
    return data;
  },

  async signUp(email: string, password: string): Promise<AuthResponse['data']> {
    // `emailRedirectTo` brings the confirmation link back to wherever the user
    // signed up (dev or prod), so they land in THIS app to be auto-signed-in.
    // The origin must be in the Supabase "Redirect URLs" allowlist.
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      // `locale` picks the confirmation email's language (web/public/email).
      options: { emailRedirectTo: window.location.origin, data: { locale: currentUiLocale() } },
    });
    if (error) throw toFailure(error);
    // Supabase returns a success shape with an empty `identities` list for a
    // duplicate email (anti-enumeration). Trade that for honest UX so the
    // user isn't left waiting for a confirmation email that never arrives.
    const user = data.user;
    if (user && (user.identities?.length ?? 0) === 0) {
      throw new AuthFailure('emailTaken');
    }
    return data;
  },

  /** Re-sends the signup confirmation email (e.g. the user never received it). */
  async resendConfirmation(email: string): Promise<void> {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) throw toFailure(error);
  },

  async signOut(): Promise<void> {
    await supabase.auth.signOut();
  },

  /** Triggers GoTrue's password-recovery email. Swallows "user not found". */
  async sendPasswordResetEmail(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/?recovery=1`,
    });
    if (error) throw toFailure(error);
  },

  /** Sets a new password for the currently signed-in (recovery) session.
   *  If GoTrue wants the two-factor code first (`insufficient_aal`), the
   *  session is refreshed so the router's challenge takes over
   *  (mfa-contract §3.3) and `mfaRequired` is thrown.
   *  On success every OTHER session is signed out (owner decision 3: a reset
   *  is the moment someone suspects compromise); returns whether that
   *  worked, so the UI only says so when it did. */
  async updatePassword(newPassword: string): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new AuthFailure('notSignedIn');
    if (!meetsPasswordPolicy(newPassword)) throw new AuthFailure('passwordTooShort');
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      if ((error as { code?: string }).code === 'insufficient_aal') {
        try { await supabase.auth.refreshSession(); } catch { /* offline: the next request gates */ }
        throw new AuthFailure('mfaRequired');
      }
      throw toFailure(error);
    }
    return signOutOtherSessions();
  },

  /** Changes the signed-in user's password via the SECURITY DEFINER RPC.
   *  On success every OTHER session is signed out (owner decision 3);
   *  returns whether that worked. */
  async changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    const user = userData.user;
    if (!user) throw new AuthFailure('notSignedIn');
    const { error } = await supabase.rpc('change_my_password', {
      p_user_id: user.id,
      p_current_password: currentPassword,
      p_new_password: newPassword,
    });
    if (error) throw toFailureFromPostgrest(error.code, error.message);
    return signOutOtherSessions();
  },

  /** Deletes the signed-in user's account after verifying the password. */
  async deleteOwnAccount(password: string): Promise<void> {
    const { data: userData } = await supabase.auth.getUser();
    const user = userData.user;
    if (!user) throw new AuthFailure('notSignedIn');
    // Migration 177 (contract §5): verify the password and list the user's
    // own audio files, remove them while the JWT still may, then erase.
    // Wrong password / not signed in stop here — nothing deleted yet. On a
    // server without 177 (PGRST202/42883) the step is skipped.
    const prep = await supabase.rpc('prepare_account_deletion', { p_password: password });
    if (prep.error) {
      if (!isMissingRpc(prep.error)) throw toFailureFromPostgrest(prep.error.code, prep.error.message);
    } else {
      const paths = (prep.data as string[] | null) ?? [];
      // No ack: files removed now never enter the queue (the trigger checks
      // the object exists). Failures are swallowed; erasure queues the rest.
      await removePrivateAudio(paths);
    }
    const { error } = await supabase.rpc('delete_my_account', {
      p_user_id: user.id,
      p_password: password,
    });
    if (error) throw toFailureFromPostgrest(error.code, error.message);
    // Server dropped the row; clear local session so the router routes to
    // Welcome. Sign-out can be flaky on this stack — ignore failures.
    try {
      await supabase.auth.signOut();
    } catch {
      /* account already gone server-side */
    }
  },
};

// ---------------------------------------------------------------------------
// Two-factor (TOTP) — port of lib/repositories/mfa_repository.dart
// (mfa-contract §0–§2, §5). GoTrue's calls behind plain types; the secret and
// URI are returned to the caller only, never logged.
// ---------------------------------------------------------------------------

/** One authenticator of the signed-in user (plain view of GoTrue's Factor). */
export interface MfaFactorInfo {
  id: string;
  verified: boolean;
  totp: boolean;
  createdAt: Date;
}

/** What the enrol step shows: dropped when the flow closes. */
export interface MfaEnrolment {
  factorId: string;
  secret: string;
  uri: string;
}

export type MfaFailureKind = 'wrongCode' | 'expired' | 'tooMany' | 'generic' | 'notEnabled';

/** A failed two-factor step, already mapped per contract §5. */
export class MfaFailure extends Error {
  readonly kind: MfaFailureKind;
  constructor(kind: MfaFailureKind) {
    super(`MfaFailure(${kind})`);
    this.name = 'MfaFailure';
    this.kind = kind;
  }
}

export function localizeMfaFailure(f: MfaFailure, t: TFunction): string {
  switch (f.kind) {
    case 'wrongCode': return t('mfaErrorWrongCode');
    case 'expired': return t('mfaErrorExpired');
    case 'tooMany': return t('mfaErrorTooMany');
    default: return t('mfaErrorGeneric');
  }
}

/** Any error of a two-factor step → its message. */
export function mfaErrorMessage(e: unknown, t: TFunction): string {
  return e instanceof MfaFailure ? localizeMfaFailure(e, t) : t('mfaErrorGeneric');
}

function gotrueAction(e: unknown): MfaErrorAction {
  const o = (e ?? {}) as { code?: unknown; status?: unknown };
  return mfaErrorAction({
    source: 'gotrue',
    code: typeof o.code === 'string' ? o.code : null,
    status: typeof o.status === 'number' ? o.status : null,
  });
}

function failureFor(action: MfaErrorAction): MfaFailure {
  switch (action) {
    case 'wrongCode': return new MfaFailure('wrongCode');
    case 'tooMany': return new MfaFailure('tooMany');
    case 'hideFeature': return new MfaFailure('notEnabled');
    default: return new MfaFailure('generic');
  }
}

/** `signOut({ scope: 'others' })` (owner decision 3): after turning
 *  two-factor on, a password reset and a password change. Best effort —
 *  true when the other sessions were ended. */
export async function signOutOtherSessions(): Promise<boolean> {
  try {
    const { error } = await supabase.auth.signOut({ scope: 'others' });
    return !error;
  } catch {
    return false;
  }
}

const ISSUER = 'Veritas';
const FRIENDLY_NAME = 'Authenticator app';

export const mfaRepository = {
  /** mfa_status() — null when the server has no two-factor yet (PGRST202 /
   *  404): hide the feature. Other errors propagate (the caller treats them
   *  as off for now and asks again next time). */
  async status(): Promise<MfaStatus | null> {
    const { data, error } = await supabase.rpc('mfa_status');
    if (error) {
      if (isMissingRpc(error) || error.code === '404') return null;
      throw error;
    }
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return parseMfaStatus(data as Record<string, unknown>);
    }
    return null;
  },

  /** Every factor of the user (GoTrue reads /user, fresh). */
  async listFactors(): Promise<MfaFactorInfo[]> {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    return (data?.all ?? []).map((f) => ({
      id: f.id,
      verified: f.status === 'verified',
      totp: f.factor_type === 'totp',
      createdAt: new Date(f.created_at),
    }));
  },

  /** The factor the challenge uses: the newest verified authenticator. */
  async verifiedTotpFactor(): Promise<MfaFactorInfo | null> {
    const all = (await this.listFactors())
      .filter((f) => f.verified && f.totp)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    return all[0] ?? null;
  },

  async removeUnverified(): Promise<void> {
    for (const f of await this.listFactors()) {
      if (f.verified) continue;
      try {
        await supabase.auth.mfa.unenroll({ factorId: f.id });
      } catch {
        // GoTrue expires unverified factors anyway.
      }
    }
  },

  /** Steps 1–2 of contract §2: remove stale unverified factors, enrol. */
  async startEnrolment(): Promise<MfaEnrolment> {
    for (let attempt = 0; ; attempt++) {
      let action: MfaErrorAction;
      try {
        await this.removeUnverified();
        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          issuer: ISSUER,
          friendlyName: FRIENDLY_NAME,
        });
        if (!error && data && data.type === 'totp') {
          return { factorId: data.id, secret: data.totp.secret, uri: data.totp.uri };
        }
        action = error ? gotrueAction(error) : 'generic';
      } catch (e) {
        action = gotrueAction(e);
      }
      if (action === 'cleanupRetry' && attempt === 0) continue;
      throw failureFor(action);
    }
  },

  /** Best effort: the user left the flow before the code was verified. */
  async cancelEnrolment(factorId: string): Promise<void> {
    try {
      await supabase.auth.mfa.unenroll({ factorId });
    } catch { /* GoTrue expires unverified factors */ }
  },

  /** challenge + verify. On success GoTrue issues aal2 tokens and emits
   *  MFA_CHALLENGE_VERIFIED. An expired challenge or an IP change is
   *  re-challenged once before it is reported. */
  async verifyCode(factorId: string, code: string): Promise<void> {
    for (let attempt = 0; ; attempt++) {
      let err: unknown;
      try {
        const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
        if (!error) return;
        err = error;
      } catch {
        throw new MfaFailure('generic');
      }
      const action = gotrueAction(err);
      if (action === 'rechallenge') {
        if (attempt === 0) continue;
        throw new MfaFailure((err as { code?: string }).code === 'mfa_challenge_expired' ? 'expired' : 'generic');
      }
      if (action === 'refreshSession') await this.refreshSession();
      throw failureFor(action);
    }
  },

  /** Contract §2 "Turn off": a fresh code (even at aal2), then remove the
   *  factor, then refresh the session. */
  async turnOff(factorId: string, code: string): Promise<void> {
    await this.verifyCode(factorId, code);
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) throw failureFor(gotrueAction(error));
    await this.refreshSession();
  },

  signOutOthers: signOutOtherSessions,

  async refreshSession(): Promise<void> {
    try {
      await supabase.auth.refreshSession();
    } catch { /* offline: the next request asks again */ }
  },

  /** "Sign out" on the challenge: this device only (contract §3.2; the
   *  gotrue-dart default). supabase-js would default to every session. */
  async signOut(): Promise<void> {
    await supabase.auth.signOut({ scope: 'local' });
  },
};
